import { Field, Label } from '@headlessui/react';
import React, { FC, useRef } from 'react';

import MinusIcon from '../../../assets/icons/minus-icon';
import { Checkbox } from '../../../components/checkbox/index';
import { useTableSort } from '../../../components/table/hooks/use-table-sort.hook';
import { Table } from '../../../components/table/index';
import {
  Stakeholder,
  StakeholderOrder,
  StakeholderRole,
  StakeholderSortBy,
} from '../../../types/stakeholder.types';
import {
  Column,
  columnOrderMap,
  columnToStakeholderSortColumnMap,
  columnTranslationKey,
} from './stakeholder-table.types';
import StakeholderTableRow from './stakeholder-table-item';

export type StakeholderTableProps = {
  accountId: string;
  data: Stakeholder[] | undefined;
  handleCancelInvitation: (id: string, subEmail?: string) => void;
  handleDelete: (id: string, subEmail?: string, hasSubEmails?: boolean) => void;
  handleEdit: (id: string) => void;
  handleEditTermination: (id: string) => void;
  handleGrantAccess: (id: string, hasSubEmails?: boolean) => void;
  handleMakeContributor: (id: string, hasSubEmails?: boolean) => void;
  handleRemoveContributorRole: (id: string, hasSubEmails?: boolean) => void;
  handleResendInvitation: (id: string, subEmail?: string) => void;
  handleRevokeAccess: (id: string, hasSubEmails?: boolean) => void;
  handleSendInvitation: (id: string, subEmail?: string) => void;
  handleTerminate: (id: string) => void;
  handleView: (id: string) => void;
  isSelected: (stakeholderId: string) => boolean;
  isSelectStarted: boolean;
  onRemoveSelect: (stakeholderId: string) => void;
  onRemoveSelectAll: () => void;
  onSelect: (stakeholderId: string) => void;
  onSelectAll: (stakeholderId: string[]) => void;
  onSortClick: (field: StakeholderSortBy) => void;
  roleInCompany?: StakeholderRole;
  searchValue?: string;
  sortField: null | StakeholderSortBy;
  sortMode: null | StakeholderOrder;
};

const StakeholderTable: FC<StakeholderTableProps> = ({
  data,
  onSortClick,
  sortField,
  handleEdit,
  handleView,
  handleDelete,
  handleCancelInvitation,
  handleGrantAccess,
  handleMakeContributor,
  handleRemoveContributorRole,
  handleResendInvitation,
  handleRevokeAccess,
  handleTerminate,
  handleEditTermination,
  handleSendInvitation,
  isSelected,
  onRemoveSelect,
  onSelect,
  onRemoveSelectAll,
  onSelectAll,
  isSelectStarted,
  roleInCompany,
  accountId,
}) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [columnsWithSortTitle] = useTableSort<Column>(
    Object.keys(columnToStakeholderSortColumnMap),
    columnTranslationKey,
    ({ field }) => {
      const newSortColumn = field ? columnToStakeholderSortColumnMap[field] : null;
      const oldSortColumn = sortField;

      if (!newSortColumn) return oldSortColumn ? onSortClick(oldSortColumn) : undefined;

      onSortClick(newSortColumn);
    },
  );

  return (
    <div className="flex gap-2 overflow-hidden rounded-md">
      <div className="flex h-full w-full flex-col items-center gap-3 rounded-md border-y border-gray-100 max-lg:hidden">
        <div className="w-full overflow-x-auto">
          <Table
            columnOrder={columnOrderMap}
            columnsTitle={{
              ...columnsWithSortTitle,
              [Column.CHECK_BOX]: (
                <Field className="flex w-fit cursor-pointer items-center">
                  <Checkbox
                    checked={isSelectStarted}
                    id="select-all"
                    onChange={(checked) =>
                      checked ? onSelectAll(data?.map(({ id }) => id) || []) : onRemoveSelectAll()
                    }
                    selectIcon={<MinusIcon />}
                  />
                  <Label
                    className="absolute left-0 top-0 h-full w-full cursor-pointer"
                    htmlFor="select-all"
                  />
                </Field>
              ),
            }}
            ref={tableRef}
            tHeadClassNames={{
              thClassName: {
                [Column.CHECK_BOX]: 'shrink-0 w-[50px] px-0',
                [Column.FULL_NAME]: 'w-[200px]',
                [Column.EMAIL]: 'w-[200px]',
                [Column.TYPE]: 'w-[200px] w-[50px]',
                [Column.ACTION]: 'justify-center',
              },
              contentClassName: {
                [Column.CHECK_BOX]: 'justify-center',
              },
            }}
          >
            {data?.map((stakeholder) => (
              <StakeholderTableRow
                accountId={accountId}
                className={isSelected(stakeholder.id) ? 'bg-gray-25' : ''}
                handleCancelInvitation={handleCancelInvitation}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                handleEditTermination={handleEditTermination}
                handleGrantAccess={handleGrantAccess}
                handleMakeContributor={handleMakeContributor}
                handleRemoveContributorRole={handleRemoveContributorRole}
                handleResendInvitation={handleResendInvitation}
                handleRevokeAccess={handleRevokeAccess}
                handleSendInvitation={handleSendInvitation}
                handleTerminate={handleTerminate}
                handleView={handleView}
                isSelected={isSelected}
                key={stakeholder.id}
                onRemoveSelect={onRemoveSelect}
                onSelect={onSelect}
                roleInCompany={roleInCompany}
                stakeholder={stakeholder}
                tableRef={tableRef}
              />
            ))}
          </Table>
        </div>
      </div>
    </div>
  );
};

StakeholderTable.displayName = 'StakeholderTable';

export default StakeholderTable;
